import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  MapPin,
  Building2,
  Shield,
  Award,
  Target,
  Briefcase,
  Calendar,
  Globe,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';

const AsiaNigerianDiaspora: React.FC = () => {
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  // Update page title and meta description
  useEffect(() => {
    document.title = "Nigerian Diaspora in Asia | Building Wealth & Supporting Nigeria";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Connect with curated investment opportunities for Nigerians in Asia. Contribute to Nigeria's development while securing profitable and transparent returns.");
    }
  }, []);

  const faqs = [
    {
      id: 1,
      question: "What investment options are available for Nigerians living in Asia?",
      answer: "Nigerians in Asia can explore agriculture, real estate, renewable energy, education, and technology-driven projects in Nigeria."
    },
    {
      id: 2,
      question: "How can I transfer money from Asia to Nigeria for investments?",
      answer: "DiasporaWise provides secure, compliant international transfer systems for Asian-based Nigerians."
    },
    {
      id: 3,
      question: "What challenges do Nigerians in Asia face when investing back home?",
      answer: "Common challenges include trust issues, fraud risks, and lack of transparency. DiasporaWise solves this with verified and monitored projects."
    },
    {
      id: 4,
      question: "Do Nigerians in Asia need large capital to start investing?",
      answer: "No, investments can start with smaller amounts, making it accessible to both professionals and entrepreneurs."
    },
    {
      id: 5,
      question: "How does DiasporaWise ensure Nigerian diaspora in Asia get updates?",
      answer: "The platform offers real-time updates, digital tracking, and customer support so investors always know how their funds are performing."
    }
  ];



  const countries = [
    { name: "China", investors: "15,000+", description: "Largest Nigerian community in Asia, strong in trade and manufacturing" },
    { name: "Japan", investors: "8,000+", description: "Growing tech and business community with focus on innovation" },
    { name: "South Korea", investors: "6,000+", description: "Strong presence in technology and entertainment industries" },
    { name: "Singapore", investors: "5,000+", description: "Financial hub with opportunities in fintech and trade" },
    { name: "Malaysia", investors: "4,000+", description: "Growing community in oil & gas and technology sectors" },
    { name: "Thailand", investors: "3,000+", description: "Emerging market with opportunities in tourism and manufacturing" }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Asian Market Expertise",
      description: "Deep understanding of Asian markets, business cultures, and regulatory environments across multiple countries."
    },
    {
      icon: Globe,
      title: "Multi-Currency Support",
      description: "Support for CNY, JPY, KRW, SGD, and other Asian currencies with competitive exchange rates."
    },
    {
      icon: Award,
      title: "Local Partnerships",
      description: "Strong partnerships with local financial institutions and business networks across Asia."
    },
    {
      icon: Users,
      title: "Pan-Asian Network",
      description: "Connect with Nigerian professionals across multiple Asian countries for networking and collaboration."
    }
  ];

  const investmentTypes = [
    {
      title: "Technology",
      description: "Startups and tech companies with Asian market access and innovation focus",
      returns: "25-45%",
      icon: Building2
    },
    {
      title: "Manufacturing",
      description: "Industrial and manufacturing projects with export potential",
      returns: "15-25%",
      icon: TrendingUp
    },
    {
      title: "Real Estate",
      description: "Premium properties in Lagos, Abuja, and other major cities",
      returns: "15-25%",
      icon: Target
    },
    {
      title: "Trade & Commerce",
      description: "Import/export businesses and trade-related ventures",
      returns: "20-35%",
      icon: Briefcase
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Simple Hero Section */}
      <section className="relative py-12 px-6 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
              <MapPin className="w-4 h-4" />
              Asia
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Nigerian Diaspora in Asia
            </h1>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
              Connect with curated investment opportunities for Nigerians in Asia. Contribute to Nigeria's development while securing profitable and transparent returns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.diasporawise.com"
                className="bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-900 transition-all duration-200 flex items-center justify-center gap-2 lg:hidden"
              >
                <Briefcase className="w-5 h-5" />
                Start Investing Today
              </a>
              <Link
                to="/contact"
                className="bg-white border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Contact Asia Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Asian Countries */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nigerian Communities Across Asia
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with Nigerian professionals in major Asian countries and access local networking opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {countries.map((country, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{country.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{country.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Investment Opportunities */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Investment Opportunities for Asian Investors
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Access to carefully curated investment opportunities with Asia-specific considerations and support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentTypes.map((investment, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <investment.icon className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{investment.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{investment.description}</p>
                  <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                    {investment.returns} Returns
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Asia-Specific Benefits */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Asia-Specific Benefits & Support
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tailored services and support designed specifically for Asian-based Nigerian investors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Asian Events & Meetups */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Upcoming Asian Events
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join our networking events and investment seminars across Asia.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                date: "March 22, 2024",
                title: "Singapore Investment Summit",
                location: "Singapore",
                description: "Connect with fellow investors and learn about new opportunities"
              },
              {
                date: "March 29, 2024",
                title: "Tokyo Tech Investment Meetup",
                location: "Tokyo, Japan",
                description: "Focus on technology and innovation investment opportunities"
              },
              {
                date: "April 5, 2024",
                title: "Seoul Business Seminar",
                location: "Seoul, South Korea",
                description: "Learn about business investment strategies and market trends"
              }
            ].map((event, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  <span className="text-orange-600 font-semibold">{event.date}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{event.location}</span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">{event.description}</p>
                <button className="w-full bg-orange-600 text-white py-3 rounded-xl font-semibold hover:bg-orange-700 transition-colors duration-300">
                  Register Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Invest from Asia?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Join thousands of successful Nigerian professionals across Asia who are building wealth through strategic investments in Nigeria.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.diasporawise.com"
              className="bg-white text-orange-700 px-8 py-4 rounded-xl font-semibold hover:bg-orange-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-2 lg:hidden"
            >
              <Target className="w-5 h-5" />
              Get Started Now
            </a>
            <a
              href="/contact"
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Contact Asia Team
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about investing in Nigeria from Asia"
        faqs={faqs}
      />

      {/* Learn More About Nigerian Diaspora Investment in Asia Section */}
      <section className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
              className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
              aria-expanded={isLearnMoreOpen}
              aria-controls="asia-diaspora-content"
            >
              <span className="font-medium">
                {isLearnMoreOpen ? 'Hide Learn More About Nigerian Diaspora Investment in Asia' : 'Learn More About Nigerian Diaspora Investment in Asia'}
              </span>
              {isLearnMoreOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          <div
            id="asia-diaspora-content"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <article className="prose prose-lg max-w-none">
              {/* Main Content Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Introduction: Nigerians Living in Asia and Why They Are Investing Back Home
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In Asia, the spread of the Nigerian community has a robust presence in China, India, Singapore, Malaysia, Japan, and even some parts of the Middle East. For many years, relatives of Nigerians living in Asia have received funds to cover the costs of education, medical expenses, and even basic survival needs.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Remittances are helpful, but not to the level of providing financial stability. This is the case for the Nigerian diaspora in Asia. Outside of money transfers, Nigerians in Asia are now able to invest in Nigeria through Diasporawise with its structured, safe, and transparent systems.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  These types of investments help the diaspora Nigerians accomplish their financial goals while aiding Nigeria's economy through job creation, infrastructure, and industrial growth.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Understanding Nigerian Diaspora Investment from Asia</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Remittances and diaspora investments differ. Remittances are more short-term. With diaspora investments, the focus is on developing sustainable assets that create a positive economic impact. For Nigerian professionals living in Asia, this means funding proven and vetted projects that generate a return on investment and support social development.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diasporawise is one of the few platforms that enables safe investment by Nigerians in Asia in Nigeria's most promising areas, including real estate, agriculture, technology, renewable energy, SMEs, and infrastructure. Such platforms are regulated and managed, and deal in legal and formal investment options, devoid of the risk factors that come with informal avenues.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  From the perspective of Nigerians in Asia, investment is an opportunity to continue to deepen cultural heritage, in addition to making sound financial decisions. It is also an opportunity to give the future generations something to hold onto.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Top Sectors for Nigerians in Asia to Invest In</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Real Estate Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For Nigerians in Asia, real estate is one of the most secure and risk-free investment options. With the population and rate of urbanization of Nigeria increasing, the supply of housing, commercial, and industrial properties is also on the rise.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The opportunities include: Residential estates and apartment complexes. Commercial offices and retail centres. Land acquisition and development projects.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Through real estate investment, Nigerians in Asia have the opportunity to earn rental income and develop sufficient housing and commercial properties in Nigeria, as well as build wealth alongside the rising stock of real estate.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Agriculture Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Agriculture is one of the most vital sectors and the backbone of the Nigerian economy. It also facilitates investment by the diaspora. Although the sector is still significantly underdeveloped and underfunded, it is essential for the development of rural areas and the economy.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Some of the most common forms of investment by Nigerians in Asia are: Raising crops and large-scale farming. Processing, packaging, and exporting foods. High export potential agribusiness activities.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Through investment in agriculture, Nigerians in the Diaspora aid in the development of the country, generate employment and make profit all at the same time.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Technology and Startups</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In Africa, the tech ecosystem in Nigeria is one of the fastest growing due to the development of fintech, health-tech, e-commerce and various digital solutions. The Nigerians in Asia are able to invest in technologies and take part in the innovations while realizing great financial profits.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Some of the opportunities in this field are: Providing financial support to early-stage startups. Enabling the growth of digital platforms with high scalability. Investing in fintech, health-tech, and e-commerce.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For diaspora investors who are given access to Asian markets and global expertise, Nigeria startup investors are able to provide support for the expansion of Nigerian startups globally, facilitating high profits and global linkages.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Investment in Renewable Energy in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria has immense opportunities for renewable energy projects due to the persistent energy deficit. This non-financial sector is most appropriate for Nigerians in Asia who are looking to earn returns while making impact in sustainable development.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Some of the investment options are: Projects on solar energy. Wind energy projects. Startups on clean energy and off-grid.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With investment in renewable energy, Nigerians in diaspora help ease access to energy, promote economic resilience and strengthen sustainable development.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">SMEs and Infrastructure</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In Nigeria's economy, SMEs and infrastructure are both fundamental and critical. For Nigerians based in Asia, investment in these sectors fosters entrepreneurship and promotes the image of the nation.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Some of the areas include: Financing and expansion of SMEs. Investment in transport and logistics. Investment in communication and digital infrastructure.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investing in SMEs and infrastructure in Nigeria helps in job creation, industrial advancement, and long-term economic growth.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits of Diaspora Investment for Nigerians Professionals in Asia</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investing in Nigeria from Asia has numerous benefits:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Wealth creation</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Establish investments and assets beyond one-time remittance.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Secured investments</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Platforms such as Diasporawise are regulated and provide safe investment.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nation building</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Contribute to economic development of Nigeria by providing jobs, investments and other services.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural attachment</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Maintain link to Nigeria while living abroad.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Legacy</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Provide wealth and a lasting legacy for the children and the generations to come.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In the case of the majority of Nigerians living in Asia, diaspora investment offers financial security, together with a strong sense of patriotism.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Start Investing in Nigeria from Asia</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With platforms such as Diasporawise, investing in Nigeria from Asia becomes simple, safe, and clear. The process is designed to be simple and direct.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">For diaspora investors based in Asia:</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  1. Pick Diasporawise. It is a regulated investment tailor-made for diaspora investors.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  2. Pick one to invest in: real estate, renewables, agriculture, tech, or SMEs.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  3. Examine your prospective returns, risks, and details.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  4. Use routed, stable digital payment channels to effectuate your investment.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  5. Receive and analyze your investment and associate documents regularly.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Through this process, Nigerians in Asia are able to invest and grow their funds, while simultaneously giving back to Nigeria.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Future of Nigerian Diaspora Investment in Asia</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investments of Nigerians in Asia are likely to become some of the earliest and most active in diaspora-focused investment. The future of diaspora investment is bright and will benefit most from Nigerians in Asia. The following will spur this growth:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fintech platforms like Diasporawise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Simplify global investment.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Continuing renewable energy projects</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Will sustain investment over the long-term.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Government policy incentives</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Encourage diaspora investment.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Connectivity and collaboration</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Within investment opportunities of the Nigerian diaspora in Asia is on the rise.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In the next 10 years, Nigerians in Asia will bring in remittances that are over 10 times what diaspora investors are expected to contribute for the development of Nigeria economically.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Building Wealth and Strengthening Nigeria</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  All Nigerians holding investments in Asia help build and strengthen Nigeria. For the Nigerians residing in Asia, investing in their country is also a matter of preserving the country's development, cultural ties, and legacy apart from making profits.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Profits can be very high when investing in real estate, agriculture, technology, renewable energy, and SMEs. Nigerians in Asia can invest in Nigeria's growth quite securely and transparently, thanks to reliable platforms like Diasporawise.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AsiaNigerianDiaspora;
